import {useCompanyTariffGuard} from '../../../../modules/company-tariff';
import {FC, useContext, useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useSetRecoilState} from 'recoil';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import {Box, Button, Divider, Modal, Stack, Toolbar, Tooltip, Typography} from '@mui/material';
import {IDeviceTypesDict, IDeviceTypesDictPostBody} from '../../../../shared/types/deviceTypesDict.types';
import ControlledInput from '../../../../ui/controlled-input/ControlledInput';
import {DictionaryService} from '../../../../api/services/dictionary.service';
import {deviceTypesDictQueryTrigger} from '../../../../store/deviceTypes/atoms';
import {EventBusContext} from '../../../../providers/EventBus';
import {transliterateRuToUpperCase} from '../../../../shared/helpers/transliteration';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  width: 500,
  p: 2,
};

interface IModalNewOrUpdateSubtype {
  open: boolean;
  handleClose: () => void;
  variant: 'UPDATE' | 'POST';
  subtype?: IDeviceTypesDict;
  deviceType?: IDeviceTypesDict;
}

export const ModalNewOrUpdateSubtype: FC<IModalNewOrUpdateSubtype> = ({
  deviceType,
  open,
  handleClose,
  variant,
  subtype,
}) => {
  const {
    control,
    formState: {isDirty},
    trigger,
    getValues,
    setValue,
    watch,
  } = useForm<IDeviceTypesDictPostBody>({
    defaultValues: {
      name: subtype?.name,
      code: subtype?.code,
      higherCode: deviceType?.code || subtype?.higherCode,
      description: subtype?.description,
    },
  });
  const triggerDeviceTypesQuery = useSetRecoilState(deviceTypesDictQueryTrigger);
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const [loading, setLoading] = useState(false);
  const {bus} = useContext(EventBusContext);

  const name = watch('name');

  const sideEffect = async () => {
    setLoading(true);
    const data = getValues();

    try {
      variant === 'POST'
        ? await DictionaryService.createDeviceType(data)
        : await DictionaryService.updateDeviceType(data);

      triggerDeviceTypesQuery((v) => v + 1);
      setTimeout(() => {
        setLoading(false);
        handleClose();
      }, 0);
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  useEffect(() => {
    !subtype && setValue('code', transliterateRuToUpperCase(name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  return (
    <Modal open={open}>
      <Box sx={style}>
        <Toolbar
          sx={{
            pl: {sm: 1},
            pr: {xs: 1, sm: 1},
          }}
        >
          <Typography sx={{flex: '1 1 100%'}} variant="h6" id="formTitle" component="div">
            {variant === 'POST' ? 'Новый подтип' : 'Редактирование подтипа'}
          </Typography>
        </Toolbar>
        <Divider sx={{m: '10px 0'}} />
        <Stack direction="column" spacing={3}>
          <Stack direction="row" spacing={3}>
            {subtype ? (
              <>
                <Tooltip title={subtype.name}>
                  <Box>
                    <ControlledInput
                      control={control}
                      name="name"
                      size="small"
                      label="Название"
                      disabled={loading}
                      rules={{
                        required: 'Обязательно для заполнения',
                      }}
                    />
                  </Box>
                </Tooltip>
                <Tooltip title={subtype.code}>
                  <Box>
                    <ControlledInput control={control} name="code" size="small" label="Код подтипа" disabled />
                  </Box>
                </Tooltip>
              </>
            ) : (
              <>
                <ControlledInput
                  control={control}
                  name="name"
                  size="small"
                  label="Название"
                  disabled={loading}
                  rules={{
                    required: 'Обязательно для заполнения',
                  }}
                />
                <ControlledInput control={control} name="code" size="small" label="Код подтипа" disabled />
              </>
            )}
          </Stack>
          <Tooltip title={subtype?.higherCode || deviceType?.code}>
            <Box>
              <ControlledInput
                control={control}
                name="higherCode"
                size="small"
                disabled
                label="Код подтипа"
                rules={{
                  required: 'Обязательно для заполнения',
                }}
                sx={{width: '100%'}}
              />
            </Box>
          </Tooltip>
          <ControlledInput
            control={control}
            name="description"
            rules={{
              maxLength: {
                value: 400,
                message: 'Максимум 400 символов',
              },
            }}
            disabled={loading}
            label="Комментарий"
            multiline
            rows={4}
            additionalFunc={() => {
              trigger('description');
            }}
          />
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Button variant="outlined" color="error" disabled={loading} onClick={handleClose}>
              Отменить
            </Button>
            <LoadingButton
              color="primary"
              variant="contained"
              disabled={(variant === 'POST' && tariffLoading) || (!isDirty && variant === 'UPDATE')}
              onClick={() => {
                if (variant === 'POST' && !checkTariff()) return;
                trigger().then((bool) => {
                  bool && sideEffect();
                });
              }}
              loading={loading}
            >
              {variant === 'POST' ? 'Добавить' : 'Сохранить'}
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};
